import { useNavigate } from 'react-router-dom'
import { DateField } from '@internal/ui-kit/components/admin'
// Reuses `apps/orders`' own cart-builder/checkout Sheet rather than a
// per-app copy (ticket feedback: "re-use same component" / "parametrize
// the component with user_id ... so that we can use exactly same
// component") — an intentional exception to this repo's usual "apps don't
// share code across their own folders" default (see that app's
// MIGRATION-NOTES.md), since the ticket driving this page explicitly asks
// for the one component, not a duplicate.
import { ManageUserCartDialog } from '@/apps/orders/ManageUserCartDialog'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@internal/ui-kit/components/ui/table'
import { fromApiUtc } from '@internal/ui-kit/lib/fromApiUtc'
import { useUserOrders } from './useUserProfileData'

/**
 * The combined page's "Orders" tab (HATCH-101, `retool-exports/user-profile/`
 * `getUserOrders` — `user-profile`'s "orders" `Screen`, `table2`). Split out
 * of the original combined "Orders & appointments" tab into its own tab per
 * ticket feedback (round 4). Each row navigates to its own order detail page
 * (`apps/orders`' `OrderDetailPage`, `/orders/:id/show`) — a row-level
 * `onClick` (round 5 feedback: "tap on order row should open order detail
 * page"), not just a link on the id cell.
 *
 * HATCH-268 folds the standalone "Cart" tab's "Manage user cart" button
 * (formerly `UserProfilePage`'s own `cart` tab) into this tab's toolbar
 * instead — ticket: "so basically it's logical to see existing orders of
 * user, manage cart, see list of orders got updated" — so the cart action
 * and the order list it updates live in one place. `ManageUserCartDialog`
 * already calls `useRefresh` on checkout, which re-triggers this tab's own
 * `useUserOrders` fetch.
 */
export function OrdersTab({ userId }: { userId: string }) {
  const { orders, isPending } = useUserOrders(userId)
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <ManageUserCartDialog fixedUserId={userId} />
      </div>
      {isPending ? (
        <p className="text-muted-foreground text-sm">Loading orders…</p>
      ) : orders.length === 0 ? (
        <p className="text-muted-foreground text-sm">No orders for this user.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payment method</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Discount code</TableHead>
              <TableHead>Created at</TableHead>
              <TableHead>Paid at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow
                key={order.order_id}
                className="cursor-pointer"
                onClick={() => navigate(`/orders/${order.order_id}/show`)}
              >
                <TableCell className="break-all underline">{order.order_id}</TableCell>
                <TableCell>
                  <Badge variant="outline">{order.status ?? '—'}</Badge>
                </TableCell>
                <TableCell>{order.payment_method ?? '—'}</TableCell>
                <TableCell>
                  {order.total_price ?? '—'} {order.currency_iso ?? ''}
                </TableCell>
                <TableCell>{order.discount_code ?? '—'}</TableCell>
                <TableCell>
                  {order.created_at ? <DateField record={order} source="created_at" showTime transform={fromApiUtc} /> : '—'}
                </TableCell>
                <TableCell>
                  {order.paid_at ? <DateField record={order} source="paid_at" showTime transform={fromApiUtc} /> : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
